'use client';

import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { useTranslation } from '@/hooks/useTranslation';

gsap.registerPlugin(ScrollTrigger);

const galleryImages = [
    {
        id: 1,
        before: '/images/treatments/hollywood-smile/gallery-1-before.webp',
        after: '/images/treatments/hollywood-smile/gallery-1-after.webp',
        titleKey: 'gallery.images.1.title',
        descriptionKey: 'gallery.images.1.description',
        categoryKey: 'gallery.images.1.category'
    },
    {
        id: 2,
        before: '/images/treatments/hollywood-smile/gallery-2-before.webp',
        after: '/images/treatments/hollywood-smile/gallery-2-after.webp',
        titleKey: 'gallery.images.2.title',
        descriptionKey: 'gallery.images.2.description',
        categoryKey: 'gallery.images.2.category'
    },
    {
        id: 3,
        before: '/images/treatments/hollywood-smile/gallery-3-before.webp',
        after: '/images/treatments/hollywood-smile/gallery-3-after.webp',
        titleKey: 'gallery.images.3.title',
        descriptionKey: 'gallery.images.3.description',
        categoryKey: 'gallery.images.3.category'
    },
    {
        id: 4,
        before: '/images/treatments/hollywood-smile/gallery-4-before.webp',
        after: '/images/treatments/hollywood-smile/gallery-4-after.webp',
        titleKey: 'gallery.images.4.title',
        descriptionKey: 'gallery.images.4.description',
        categoryKey: 'gallery.images.4.category'
    },
    {
        id: 5,
        before: '/images/treatments/hollywood-smile/gallery-5-before.webp',
        after: '/images/treatments/hollywood-smile/gallery-5-after.webp',
        titleKey: 'gallery.images.5.title',
        descriptionKey: 'gallery.images.5.description',
        categoryKey: 'gallery.images.5.category'
    },
    {
        id: 6,
        before: '/images/treatments/hollywood-smile/gallery-6-before.webp',
        after: '/images/treatments/hollywood-smile/gallery-6-after.webp',
        titleKey: 'gallery.images.6.title',
        descriptionKey: 'gallery.images.6.description',
        categoryKey: 'gallery.images.6.category'
    },
    {
        id: 7,
        before: '/images/treatments/hollywood-smile/gallery-7-before.webp',
        after: '/images/treatments/hollywood-smile/gallery-7-after.webp',
        titleKey: 'gallery.images.7.title',
        descriptionKey: 'gallery.images.7.description',
        categoryKey: 'gallery.images.7.category'
    },
    {
        id: 8,
        before: '/images/treatments/hollywood-smile/gallery-8-before.webp',
        after: '/images/treatments/hollywood-smile/gallery-8-after.webp',
        titleKey: 'gallery.images.8.title',
        descriptionKey: 'gallery.images.8.description',
        categoryKey: 'gallery.images.8.category'
    }
];

export default function GallerySection() {
    const { t } = useTranslation('hollywood-smile');
    const [selectedImage, setSelectedImage] = useState<number | null>(null);
    const [showBefore, setShowBefore] = useState(false);
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const gridRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Title animation
            gsap.fromTo(titleRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: "top 80%",
                        end: "bottom 20%",
                        once: true
                    }
                }
            );

            // Grid animation
            if (gridRef.current?.children) {
                gsap.fromTo(gridRef.current.children,
                    { opacity: 0, y: 60, scale: 0.9 },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.8,
                        stagger: 0.1,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: gridRef.current,
                            start: "top 80%",
                            end: "bottom 20%",
                            once: true
                        }
                    }
                );
            }

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    const openLightbox = (index: number) => {
        setSelectedImage(index);
        setShowBefore(false);
    };

    const closeLightbox = () => {
        setSelectedImage(null);
    };

    const nextImage = () => {
        if (selectedImage !== null) {
            setSelectedImage((selectedImage + 1) % galleryImages.length);
        }
    };

    const prevImage = () => {
        if (selectedImage !== null) {
            setSelectedImage((selectedImage - 1 + galleryImages.length) % galleryImages.length);
        }
    };

    return (
        <section
            id="gallery"
            ref={sectionRef}
            className="py-20 bg-slate-50 dark:bg-navy-950 relative overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-10 right-10 w-96 h-96 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute bottom-10 left-10 w-80 h-80 bg-gold rounded-full blur-2xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto mb-16">
                    <h2
                        ref={titleRef}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif"
                    >
                        {t('gallery.title.part1')}{' '}
                        <span className="text-gold relative">
                            {t('gallery.title.part2')}
                            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gold rounded-full"></div>
                        </span>
                    </h2>
                    <p className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed">
                        {t('gallery.subtitle')}
                    </p>
                </div>

                {/* Gallery Grid */}
                <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {galleryImages.map((image, index) => (
                        <motion.div
                            key={image.id}
                            className="group relative cursor-pointer"
                            whileHover={{ y: -10 }}
                            transition={{ duration: 0.3 }}
                            onClick={() => openLightbox(index)}
                        >
                            <div className="relative h-64 rounded-2xl overflow-hidden shadow-lg group-hover:shadow-2xl transition-all duration-300">
                                {/* Before / After side-by-side */}
                                <div className="grid grid-cols-2 w-full h-full">
                                    <div className="relative overflow-hidden">
                                        <OptimizedImage
                                            src={image.before}
                                            alt={`Before - ${t(image.titleKey)}`}
                                            width={300}
                                            height={300}
                                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                        />
                                        <div className="absolute top-3 left-3 bg-red-600/90 text-white px-2 py-0.5 rounded-full text-xs font-semibold">
                                            {t('gallery.labels.before')}
                                        </div>
                                    </div>
                                    <div className="relative overflow-hidden">
                                        <OptimizedImage
                                            src={image.after}
                                            alt={`After - ${t(image.titleKey)}`}
                                            width={300}
                                            height={300}
                                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                        />
                                        <div className="absolute top-3 right-3 bg-green-600/90 text-white px-2 py-0.5 rounded-full text-xs font-semibold">
                                            {t('gallery.labels.after')}
                                        </div>
                                    </div>
                                </div>

                                {/* Center divider */}
                                <div className="pointer-events-none absolute top-0 bottom-0 left-1/2 w-px bg-white/60 -translate-x-1/2" />

                                {/* Hover overlay gradient */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>

                                {/* Zoom Icon */}
                                <div className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-xs rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-0 group-hover:scale-100 pointer-events-none">
                                    <ZoomIn className="w-5 h-5 text-white" />
                                </div>

                                {/* Category Badge */}
                                <div className="absolute bottom-3 right-3 bg-gold text-white px-3 py-1 rounded-full text-xs font-medium">
                                    {t(image.categoryKey)}
                                </div>

                                {/* Content Overlay (on hover) */}
                                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/85 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                    <h3 className="text-white font-bold text-base mb-0.5 pr-24">{t(image.titleKey)}</h3>
                                    <p className="text-white/80 text-xs pr-24">{t(image.descriptionKey)}</p>
                                </div>

                                {/* Hover Border */}
                                <div className="absolute inset-0 border-2 border-gold rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Bottom CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="text-center mt-16"
                >
                    <div className="bg-gold/10 rounded-2xl p-8 border border-gold/20 max-w-3xl mx-auto">
                        <h3 className="text-2xl font-bold text-navy dark:text-white mb-4">
                            {t('gallery.cta.title')}
                        </h3>
                        <p className="text-slate-600 dark:text-slate-300 text-lg">
                            {t('gallery.cta.description')}
                        </p>
                    </div>
                </motion.div>
            </div>

            {/* Lightbox Modal */}
            <Dialog open={selectedImage !== null} onOpenChange={closeLightbox}>
                <DialogContent className="max-w-4xl w-full h-[90vh] p-0 bg-black/95 border-none">
                    <DialogTitle className="sr-only">
                        {selectedImage !== null ? `Gallery Image ${selectedImage + 1}` : ''}
                    </DialogTitle>

                    {selectedImage !== null && (
                        <div className="relative w-full h-full flex items-center justify-center">
                            {/* Close Button */}
                            <button
                                onClick={closeLightbox}
                                className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/20 backdrop-blur-xs rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>

                            {/* Navigation Buttons */}
                            <button
                                onClick={prevImage}
                                className="absolute left-4 z-10 w-12 h-12 bg-white/20 backdrop-blur-xs rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                            >
                                <ChevronLeft className="w-8 h-8" />
                            </button>
                            <button
                                onClick={nextImage}
                                className="absolute right-4 z-10 w-12 h-12 bg-white/20 backdrop-blur-xs rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                            >
                                <ChevronRight className="w-8 h-8" />
                            </button>

                            {/* Before/After Toggle */}
                            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 flex bg-white/20 backdrop-blur-xs rounded-full p-1">
                                <button
                                    onClick={() => setShowBefore(false)}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${!showBefore ? 'bg-gold text-white' : 'text-white/80 hover:text-white'
                                        }`}
                                >
                                    {t('gallery.labels.after')}
                                </button>
                                <button
                                    onClick={() => setShowBefore(true)}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${showBefore ? 'bg-gold text-white' : 'text-white/80 hover:text-white'
                                        }`}
                                >
                                    {t('gallery.labels.before')}
                                </button>
                            </div>

                            {/* Main Image */}
                            <div className="relative w-full h-full flex items-center justify-center p-8">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={`${selectedImage}-${showBefore}`}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        transition={{ duration: 0.3 }}
                                        className="relative max-w-full max-h-full"
                                    >
                                        <OptimizedImage
                                            src={showBefore ? galleryImages[selectedImage].before : galleryImages[selectedImage].after}
                                            alt={`${showBefore ? t('gallery.labels.before') : t('gallery.labels.after')} - ${t(galleryImages[selectedImage].titleKey)}`}
                                            width={800}
                                            height={600}
                                            className="max-w-full max-h-full object-contain rounded-lg"
                                        />
                                    </motion.div>
                                </AnimatePresence>
                            </div>

                            {/* Image Info */}
                            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 text-center">
                                <div className="bg-white/20 backdrop-blur-xs rounded-2xl px-6 py-3">
                                    <h3 className="text-white font-bold text-lg mb-1">
                                        {t(galleryImages[selectedImage].titleKey)}
                                    </h3>
                                    <p className="text-white/80 text-sm">
                                        {t(galleryImages[selectedImage].descriptionKey)}
                                    </p>
                                </div>
                            </div>

                            {/* Image Counter */}
                            <div className="absolute bottom-4 right-4 z-10 bg-white/20 backdrop-blur-xs rounded-full px-3 py-1 text-white text-sm">
                                {selectedImage + 1} / {galleryImages.length}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </section>
    );
} 
